import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { LayoutGrid, CalendarDays, Users, BookMarked, BookOpen, Moon, User, LogOut, ShieldCheck, History } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_disciple-hub-3/artifacts/wqhxldc5_Live%20Church%20Logo%20Print.png";

// Every "app" reachable from the global switcher — keep this list identical everywhere
const APPS = [
  { key: "home", label: "Home", path: "/home", icon: LayoutGrid, dot: "bg-slate-400", activeColor: "text-slate-600" },
  { key: "events", label: "Events", path: "/events", icon: CalendarDays, dot: "bg-amber-500", activeColor: "text-amber-500", superAdminOnly: true },
  { key: "community", label: "Community", path: "/community", icon: Users, dot: "bg-teal-500", activeColor: "text-teal-600" },
  { key: "guide", label: "Weekly Guide", path: "/guide", icon: BookMarked, dot: "bg-emerald-500", activeColor: "text-emerald-600" },
  { key: "wednesday", label: "Wednesday Nights", path: "/wednesday", icon: Moon, dot: "bg-indigo-500", activeColor: "text-indigo-600" },
  { key: "discipleship", label: "Discipleship Intensive", path: null, icon: BookOpen, dot: "bg-amber-500", activeColor: "text-amber-600", requiresIntensiveAccess: true },
  { key: "admin", label: "Admin", path: "/admin", icon: ShieldCheck, dot: "bg-rose-500", activeColor: "text-rose-600", superAdminOnly: true },
];

/** Returns whether the given user can see/use the Discipleship Intensive app */
export const hasIntensiveAccess = (user) =>
  Boolean(user?.role === "teacher" || user?.is_super_admin || user?.is_intensive_student);

/** Filters the global APPS list down to what this user is allowed to see */
const visibleApps = (user) =>
  APPS.filter((app) => {
    if (app.superAdminOnly) return Boolean(user?.is_super_admin);
    if (app.requiresIntensiveAccess) return hasIntensiveAccess(user);
    return true;
  });

export const getInitials = (name) =>
  name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

/** Static, non-interactive brand header (logo + current app name). No dropdown.
 * `collapsed` forces icon-only mode (used by Layout.jsx's manual collapse toggle).
 * `alwaysExpanded` forces the label to show regardless of viewport width (used inside mobile sheets). */
export function SidebarBrand({ brandLabel, collapsed = false, alwaysExpanded = false, onClick }) {
  const labelCls = collapsed ? "hidden" : alwaysExpanded ? "block" : "hidden xl:block";
  return (
    <div
      data-testid="sidebar-brand"
      onClick={onClick}
      className={`flex items-center gap-2 py-1.5 w-full min-w-0 ${collapsed ? "justify-center px-0 cursor-pointer" : "px-2"}`}
    >
      <img src={LOGO_URL} alt="Live Church" className={`${collapsed ? "h-8" : "h-7"} w-auto shrink-0`} />
      <span className={`font-serif font-bold text-sm text-slate-900 truncate flex-1 text-left ${labelCls}`}>
        {brandLabel}
      </span>
    </div>
  );
}

/** Always-visible "Switch App" list — replaces the old click-to-open popover. `active` = one of APPS[].key
 * `collapsed` forces icon-only mode (used by Layout.jsx's manual collapse toggle).
 * `alwaysExpanded` forces labels to show regardless of viewport width (used inside mobile sheets). */
export function SwitchAppList({ user, active, collapsed = false, alwaysExpanded = false }) {
  const navigate = useNavigate();
  const [newPostCount, setNewPostCount] = useState(0);
  const [guideHasNew, setGuideHasNew] = useState(false);
  const resolvePath = (app) =>
    app.key === "discipleship" ? (user?.role === "teacher" ? "/teacher" : "/student") : app.path;
  const labelCls = collapsed ? "hidden" : alwaysExpanded ? "block" : "hidden xl:block";
  const apps = visibleApps(user);

  useEffect(() => {
    if (!user) return;
    const fetchCount = () => {
      axios.get(`${API}/feed/new-count`).then((res) => setNewPostCount(res.data.count || 0)).catch(() => {});
      axios.get(`${API}/guides/has-new`).then((res) => setGuideHasNew(Boolean(res.data.has_new))).catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 20000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <div data-testid="switch-app-section" className="mb-1">
      <p className={`px-2.5 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider ${labelCls}`}>
        Switch App
      </p>
      <div className="space-y-0.5">
        {apps.map((app) => {
          const Icon = app.icon;
          const isActive = active === app.key;
          const showBadge = app.key === "community" && !isActive && newPostCount > 0;
          const showGuideBadge = app.key === "guide" && !isActive && guideHasNew;
          return (
            <button
              key={app.key}
              data-testid={`app-switcher-${app.key}`}
              onClick={() => navigate(resolvePath(app))}
              className={`flex items-center gap-2.5 w-full py-2 rounded-md text-sm transition-colors ${
                collapsed ? "justify-center px-2" : "px-2.5"
              } ${isActive ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-600 hover:bg-slate-100"}`}
            >
              <div className="relative shrink-0">
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? app.activeColor : "text-slate-400"}`} />
                {showBadge && (
                  <span
                    data-testid="community-new-post-badge"
                    className="absolute -top-1.5 -right-2 min-w-[14px] h-3.5 rounded-full bg-teal-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5"
                  >
                    {newPostCount > 99 ? "99+" : newPostCount}
                  </span>
                )}
                {showGuideBadge && (
                  <span
                    data-testid="guide-new-badge"
                    className="absolute -top-1.5 -right-3 min-w-[24px] h-3.5 rounded-full bg-emerald-500 text-white text-[8px] font-bold tracking-wide flex items-center justify-center px-1"
                  >
                    NEW
                  </span>
                )}
              </div>
              <span className={`truncate ${labelCls}`}>{app.label}</span>
              {isActive && <span className={`ml-auto w-1.5 h-1.5 rounded-full ${app.dot} shrink-0 ${labelCls}`} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function NavItem({ icon: Icon, label, onClick, active = false, badge = 0 }) {
  return (
    <button
      data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors w-full text-left group ${
        active ? "bg-amber-50 text-amber-700 font-semibold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <div className="relative">
        <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? "text-amber-600" : "text-slate-400 group-hover:text-slate-600"}`} />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span className="text-sm hidden xl:block">{label}</span>
    </button>
  );
}

/** Simple fixed mobile bar shown when the desktop sidebar is hidden */
export function MobileTopBar({ brandLabel }) {
  const navigate = useNavigate();
  return (
    <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100 h-14 flex items-center justify-between px-4">
      <div className="flex items-center gap-2">
        <img src={LOGO_URL} alt="Live Church" className="h-7 w-auto" />
        <span className="font-serif font-bold text-sm text-slate-900">{brandLabel}</span>
      </div>
      <Button variant="ghost" size="sm" data-testid="mobile-switch-app-btn" onClick={() => navigate("/home")} className="text-xs">
        Switch App
      </Button>
    </header>
  );
}

/**
 * Full desktop left sidebar: app switcher + page nav + user menu.
 * Renders nothing on mobile (< md) — pair with <MobileTopBar /> for small screens.
 */
export default function AppSidebar({ user, active, brandLabel, navItems = [], profilePath = "/profile" }) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } finally {
      navigate("/");
    }
  };

  return (
    <aside
      data-testid="app-sidebar"
      className="hidden md:flex flex-col fixed top-0 left-0 h-screen w-[68px] xl:w-[260px] shrink-0 bg-white border-r border-slate-200 px-2 xl:px-4 py-4 overflow-y-auto z-40"
    >
      <div className="h-16 shrink-0 flex items-center px-1 mb-1">
        <SidebarBrand brandLabel={brandLabel} />
      </div>

      <SwitchAppList user={user} active={active} />

      <Separator className="mb-3" />

      <p className="px-2.5 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider hidden xl:block">
        Current App
      </p>
      <nav className="flex flex-col gap-0.5 flex-1 relative">
        {navItems.map((item) => (
          <NavItem key={item.label} {...item} />
        ))}
      </nav>

      <Separator className="my-3" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="sidebar-user-menu"
            className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors w-full"
          >
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-amber-100 text-amber-700 text-xs font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 text-left hidden xl:block">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-52" side="top" align="start">
          <DropdownMenuItem data-testid="sidebar-profile-link" onClick={() => navigate(profilePath)}>
            <User className="w-4 h-4 mr-2" /> My Profile
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="sidebar-versions-link" onClick={() => navigate("/versions")}>
            <History className="w-4 h-4 mr-2" /> Versions
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="sidebar-logout-btn" onClick={handleLogout} className="text-red-600 focus:text-red-600">
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </aside>
  );
}
